import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, RotateCcw, Smartphone, ShieldCheck, Pencil, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

const normalizeMobile = (raw) => {
  let d = raw.replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return d;
};

const maskMobile = (m) => `+91 ${m.slice(0, 2)}•••••${m.slice(-4)}`;

export default function LoginModal() {
  const { loginOpen, setLoginOpen, requestOtp, verifyOtp, finishLogin, refreshMe } = useStore();
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [devOtp, setDevOtp] = useState(null);
  const [expiresIn, setExpiresIn] = useState(600);
  const [verifiedCustomer, setVerifiedCustomer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (!loginOpen) {
      setStep(1); setOtp(""); setName(""); setEmail(""); setDevOtp(null); setResendIn(0); setVerifiedCustomer(null);
    }
  }, [loginOpen]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const sendOtp = async () => {
    const m = normalizeMobile(mobile);
    if (!/^[6-9]\d{9}$/.test(m)) {
      toast.error("Enter a valid 10-digit Indian mobile number");
      return;
    }
    setBusy(true);
    try {
      const data = await requestOtp(m);
      setMobile(m);
      setExpiresIn(data.expires_in || 600);
      if (data.dev_mode && data.dev_otp) {
        setDevOtp(data.dev_otp);
        setOtp(data.dev_otp);
      } else {
        setDevOtp(null);
      }
      setStep(2);
      setResendIn(60);
      toast.success(data.message || `OTP sent to +91 ${m}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const verify = async () => {
    if (!/^\d{4,6}$/.test(otp)) { toast.error("Enter the OTP from the SMS (4–6 digits)"); return; }
    setBusy(true);
    try {
      const data = await verifyOtp(mobile, otp);
      if (data.is_new_user) {
        setVerifiedCustomer(data.customer);
        setStep(3);
      } else {
        finishLogin(data.customer);
      }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const saveProfile = async () => {
    setBusy(true);
    try {
      if (name.trim() || email.trim()) {
        await api.patch("/ecom/auth/me", {
          ...(name.trim() ? { name: name.trim() } : {}),
          ...(email.trim() ? { email: email.trim() } : {}),
        });
        const fresh = await refreshMe();
        finishLogin(fresh);
      } else {
        finishLogin(verifiedCustomer);
      }
    } catch {
      toast.error("Couldn't save your details — you can add them later in My Account");
      finishLogin(verifiedCustomer);
    }
    setBusy(false);
  };

  return (
    <AnimatePresence>
      {loginOpen && (
        <>
          <motion.div
            data-testid="auth-backdrop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLoginOpen(false)}
            className="fixed inset-0 z-[95] bg-cream/40 backdrop-blur-sm"
          />
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              data-testid="auth-modal"
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.97 }}
              transition={{ type: "spring", stiffness: 260, damping: 26 }}
              className="max-h-[88vh] w-full max-w-md overflow-y-auto rounded-3xl border border-champagne/50 bg-pine shadow-2xl shadow-gold/20 no-scrollbar"
            >
              <div className="flex items-center justify-between border-b border-champagne/40 p-5">
                <img src="/assets/soonyoung-logo-trim.png" alt="Soonyoung" className="h-6 w-auto" />
                <button data-testid="auth-close-btn" onClick={() => setLoginOpen(false)} className="flex h-9 w-9 items-center justify-center rounded-full border border-champagne/40 text-cream hover:border-gold hover:text-gold" aria-label="Close login">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="p-6 sm:p-8">
                <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-gold">
                  {step === 1 && "Secured by OTP · No passwords, ever"}
                  {step === 2 && `Valid for ${Math.round(expiresIn / 60)} minutes`}
                  {step === 3 && "Optional · first time only"}
                </p>
                <h2 className="mt-2 font-display text-2xl tracking-tight text-cream sm:text-3xl">
                  {step === 1 && "Sign in or create your account"}
                  {step === 2 && (
                    <span className="flex flex-wrap items-center gap-2 text-xl sm:text-2xl">
                      Sent to {maskMobile(mobile)}
                      <button data-testid="auth-change-mobile" onClick={() => { setStep(1); setOtp(""); }} className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold hover:underline">
                        <Pencil className="h-3 w-3" /> Change number?
                      </button>
                    </span>
                  )}
                  {step === 3 && "Welcome to the Sanctuary"}
                </h2>

                {step === 1 && (
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center gap-3 rounded-2xl border border-champagne/50 bg-forest px-4 py-3.5 focus-within:border-gold">
                      <Smartphone className="h-4 w-4 shrink-0 text-gold" />
                      <span className="font-mono text-sm text-sage">+91</span>
                      <input
                        data-testid="auth-mobile-input"
                        type="tel" inputMode="numeric" maxLength={13}
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value.replace(/[^\d+]/g, "").slice(0, 13))}
                        onKeyDown={(e) => e.key === "Enter" && sendOtp()}
                        placeholder="10-digit mobile number"
                        className="w-full bg-transparent text-sm text-cream placeholder:text-fern focus:outline-none"
                      />
                    </div>
                    <button
                      data-testid="auth-send-otp-btn"
                      onClick={sendOtp} disabled={busy}
                      className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest transition-transform hover:scale-[1.02] disabled:opacity-60"
                    >
                      {busy ? "Sending…" : "Send OTP"} <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {step === 2 && (
                  <div className="mt-6 space-y-4">
                    <input
                      data-testid="auth-otp-input"
                      type="tel" inputMode="numeric" maxLength={6} autoComplete="one-time-code" autoFocus
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      onKeyDown={(e) => e.key === "Enter" && verify()}
                      placeholder="••••••"
                      className="w-full rounded-2xl border border-champagne/50 bg-forest px-4 py-3.5 text-center font-mono text-2xl tracking-[0.5em] text-cream placeholder:text-fern focus:border-gold focus:outline-none"
                    />
                    {devOtp && (
                      <p data-testid="auth-dev-otp-hint" className="rounded-xl bg-moss px-4 py-2.5 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                        Dev mode — OTP is {devOtp}
                      </p>
                    )}
                    <button
                      data-testid="auth-verify-btn"
                      onClick={verify} disabled={busy}
                      className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest transition-transform hover:scale-[1.02] disabled:opacity-60"
                    >
                      {busy ? "Verifying…" : "Verify & Continue"} <ShieldCheck className="h-4 w-4" />
                    </button>
                    <button
                      data-testid="auth-resend-btn"
                      onClick={sendOtp} disabled={resendIn > 0 || busy}
                      className="flex w-full items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-sage hover:text-gold disabled:opacity-50"
                    >
                      <RotateCcw className="h-3 w-3" /> {resendIn > 0 ? `Didn't get it? Resend in ${resendIn}s` : "Didn't get it? Resend OTP"}
                    </button>
                  </div>
                )}

                {step === 3 && (
                  <div className="mt-6 space-y-4">
                    <p className="flex items-center gap-2 rounded-xl bg-moss px-4 py-3 text-xs leading-relaxed text-sage">
                      <Sparkles className="h-4 w-4 shrink-0 text-gold" />
                      Just once — tell us your name so we can greet you properly. Returning shoppers skip this forever.
                    </p>
                    <input
                      data-testid="auth-name-input"
                      type="text" autoFocus
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name (optional)"
                      className="w-full rounded-2xl border border-champagne/50 bg-forest px-4 py-3.5 text-sm text-cream placeholder:text-fern focus:border-gold focus:outline-none"
                    />
                    <input
                      data-testid="auth-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email (optional, for order updates)"
                      className="w-full rounded-2xl border border-champagne/50 bg-forest px-4 py-3.5 text-sm text-cream placeholder:text-fern focus:border-gold focus:outline-none"
                    />
                    <button
                      data-testid="auth-profile-save-btn"
                      onClick={saveProfile} disabled={busy}
                      className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest transition-transform hover:scale-[1.02] disabled:opacity-60"
                    >
                      {busy ? "Saving…" : "Save & Continue"} <ArrowRight className="h-4 w-4" />
                    </button>
                    <button
                      data-testid="auth-profile-skip-btn"
                      onClick={() => finishLogin(verifiedCustomer)}
                      className="w-full text-center font-mono text-[10px] uppercase tracking-[0.2em] text-fern hover:text-gold"
                    >
                      Skip for now
                    </button>
                  </div>
                )}

                <p className="mt-6 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-fern">
                  New here? Your account is created automatically on verify
                </p>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
